import { CheckCircle, Package, Calendar, Mail, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/utils/format";
import type { CheckoutResponse } from "@/types/order";

interface OrderConfirmationProps {
  order: CheckoutResponse;
}

export function OrderConfirmation({ order }: OrderConfirmationProps) {
  const formattedDate = new Intl.DateTimeFormat("es-CR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(order.createdAt));

  return (
    <div className="max-w-lg mx-auto">
      {/* Ticket Card */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary to-primary/80 p-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-500">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">¡Gracias por tu pedido!</h1>
          <p className="text-white/90">Tu pedido ha sido recibido exitosamente</p>
        </div>

        {/* Order Details */}
        <div className="p-6 space-y-6">
          {/* Order Number */}
          <div className="text-center pb-6 border-b border-dashed border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Número de Pedido</p>
            <p className="text-2xl font-bold text-primary tracking-wide">{order.orderNumber}</p>
          </div>

          {/* Order Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="font-bold text-lg">{formatPrice(order.total)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Fecha</p>
                <p className="font-medium text-sm">{formattedDate}</p>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Confirmación enviada a</p>
                <p className="font-medium">{order.customerEmail}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Cliente</p>
                <p className="font-medium">{order.customerName}</p>
              </div>
            </div>
          </div>

          {/* Barcode Simulation */}
          <div className="flex justify-center py-4">
            <div className="flex gap-[2px]">
              {Array.from({ length: 40 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-foreground"
                  style={{
                    width: Math.random() > 0.5 ? "2px" : "1px",
                    height: "40px",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Message */}
          <p className="text-center text-sm text-muted-foreground">
            Nos contactaremos pronto para coordinar el pago y la entrega de tu pedido.
          </p>
        </div>

        {/* Footer */}
        <div className="p-6 pt-0">
          <Link to="/catalogo">
            <Button className="w-full" size="lg">
              Seguir Comprando
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          ¿Tienes alguna pregunta? Escríbenos a{" "}
          <a href="mailto:docapetshopcr@gmail.com" className="text-primary hover:underline">
            docapetshopcr@gmail.com
          </a>
        </p>
      </div>
    </div>
  );
}
